/**
 * <s-announcement-rotator> — Cycles through its direct children one at a time.
 *
 * Children are stacked in one grid cell by the caller's utility classes; the
 * active child carries `data-active`, inactive ones are `inert` + aria-hidden.
 * Rotation pauses while hovered or focused and stops entirely when the visitor
 * prefers reduced motion (prev/next buttons still work).
 *
 * Attributes:
 *   interval — milliseconds between slides (default 5000).
 *
 * Commands (via commandfor/command on buttons):
 *   --next, --previous
 */
class SAnnouncementRotator extends HTMLElement {
  #ac;
  #timer;
  #index = 0;
  #paused = false;

  get #slides() {
    return [...this.querySelector('[data-slides]')?.children ?? []];
  }

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#show(0);

    this.addEventListener('command', (event) => {
      if (event.command === '--next') this.#show(this.#index + 1);
      if (event.command === '--previous') this.#show(this.#index - 1);
      this.#restart();
    }, { signal });

    const pause = () => { this.#paused = true; };
    const resume = () => { this.#paused = this.matches(':hover') || this.matches(':focus-within'); };
    this.addEventListener('pointerenter', pause, { signal });
    this.addEventListener('pointerleave', resume, { signal });
    this.addEventListener('focusin', pause, { signal });
    this.addEventListener('focusout', () => requestAnimationFrame(resume), { signal });

    this.#restart();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearInterval(this.#timer);
  }

  #restart() {
    clearInterval(this.#timer);
    if (this.#slides.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const interval = parseInt(this.getAttribute('interval'), 10) || 5000;
    this.#timer = setInterval(() => {
      if (!this.#paused) this.#show(this.#index + 1);
    }, interval);
  }

  #show(index) {
    const slides = this.#slides;
    if (!slides.length) return;
    this.#index = (index + slides.length) % slides.length;
    slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.toggleAttribute('data-active', active);
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });
  }
}

if (!customElements.get('s-announcement-rotator')) {
  customElements.define('s-announcement-rotator', SAnnouncementRotator);
}
